// frontend/src/features/onboarding/WelcomeWizard.tsx
import { useEffect, useRef, useState } from 'react';
import { Anchor, Center, Container, Group, Loader, Stack, Stepper, Text } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTank } from '@/contexts/TankContext';
import { useOnboarding } from '@/hooks/useOnboarding';
import { trackOnboarding } from '@/lib/onboardingAnalytics';
import { OnboardingWizardProvider, useOnboardingWizard } from './WizardContext';
import { ONBOARDING_STEPS } from './types';
import { CreateTankStep, CapabilityStep, FirstValueStep, FinishStep } from './steps';

/**
 * Renders the wizard content for the current step. No AppShell / nav / FAB
 * chrome — just a centered column with the brand wordmark and a progress
 * indicator, so the wizard reads as a focused, standalone flow.
 */
function WelcomeWizardContent() {
  const { user } = useAuth();
  const { tanks, isInitialized, error: tanksError } = useTank();
  const navigate = useNavigate();
  const { update } = useOnboarding();
  const { state, currentStepInfo } = useOnboardingWizard();

  // Wizard-level skip. Available from step 2 onward (NOT step 1 — the app is
  // unusable tankless), and NOT on the terminal finish step: "You're all set!"
  // is the end of the flow, so there is nothing left to skip past and the
  // affordance only competes with the step's own "Go to my dashboard" CTA.
  // Keyed off the typed step id rather than an index so reordering or inserting
  // a step cannot silently re-expose it.
  // CRITICAL: any skip past step 1 sets completed so the OnboardingGate never
  // traps the user back in /welcome. Fire the PATCH and await it briefly, but
  // navigate regardless of the result (the gate's tanks.length>0 guard is the
  // real safety net; useOnboarding.onError logs a failure).
  const canSkipSetup = state.currentStep >= 1 && currentStepInfo.id !== 'finish';
  // Guard the skip against a double-fire: the PATCH + navigate are async, so a
  // fast second click would otherwise send a redundant PATCH. The ref latches
  // synchronously so a second click within the same tick (before the state
  // re-render lands) is still rejected; the state drives the disabled + pending
  // feedback. Neither is reset — we always navigate away.
  const isSkippingRef = useRef(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const handleSkipSetup = async () => {
    if (isSkippingRef.current) return;
    isSkippingRef.current = true;
    setIsSkipping(true);
    // Funnel: a wizard-level skip abandons the current step (design §3.7).
    trackOnboarding('step_skipped', { step: currentStepInfo.id });
    try {
      await update({ completed: true });
    } catch {
      // Ignored — see FinishStep: a failed completion cannot re-trap the user.
    }
    navigate('/', { replace: true });
  };

  // Reverse guard (addition — the plan does not specify it; see report).
  // A user who lands on /welcome but is already onboarded or already owns a tank
  // (e.g. via a stale bookmark) is redirected out to the app. The decision is
  // made ONCE, when the data first becomes available, and then locked in via a
  // ref: creating a tank during step 1 must NOT bounce the user out mid-flow.
  //
  // The ref is the once-lock: it is set synchronously the moment we commit to
  // the wizard and is never cleared, so the effect can only ever make the entry
  // decision a single time. The `hasEntered` state mirrors the ref so that
  // committing to the wizard triggers a re-render (a ref mutation would not),
  // which lets `ready` (below) flip on and reveal the step content.
  //
  // `isInitialized` is true even when the tanks fetch FAILED, so it alone is
  // not enough to make the entry decision: on error `tanks` is `[]` for want of
  // data, not because the user owns none. The error guard therefore sits BEFORE
  // the latch — a failed fetch must neither enter (which would once-lock a
  // grandfathered user into the wizard, permanently, since the ref never
  // clears) nor bounce. We simply keep showing the loader until the fetch
  // recovers, then make the decision for real.
  const hasEnteredRef = useRef(false);
  const [hasEntered, setHasEntered] = useState(false);
  useEffect(() => {
    if (!user || !isInitialized) return; // wait for data
    if (tanksError) return; // failed fetch: decide nothing, lock nothing
    if (hasEnteredRef.current) return; // already committed to the wizard
    if (user.onboarding_completed_at || tanks.length > 0) {
      navigate('/', { replace: true });
      return;
    }
    hasEnteredRef.current = true;
    setHasEntered(true);
    // Funnel: the user has entered the wizard (NOT redirected away). Fired
    // exactly once — the hasEnteredRef latch above guarantees single entry, and
    // the redirect branch returns before this point.
    trackOnboarding('wizard_viewed');
  }, [user, isInitialized, tanksError, tanks.length, navigate]);

  // Only reveal step content once the entry decision has settled: either we
  // have committed to the wizard (`hasEntered`), or the data is loaded and
  // clearly identifies a fresh user (not onboarded, owns no tanks). Until then
  // an onboarded / grandfathered user would otherwise flash step 1 during the
  // tank-load window, before the reverse-guard effect redirects them.
  //
  // A failed tanks fetch blocks only the not-yet-entered branch: `tanks.length
  // === 0` is unreliable while errored, so keep the loader up. Once the user
  // HAS entered, a later transient error must not yank the wizard out from
  // under them mid-flow.
  const ready = Boolean(
    user &&
      isInitialized &&
      (hasEntered || (!tanksError && !user.onboarding_completed_at && tanks.length === 0))
  );

  const renderStep = () => {
    switch (currentStepInfo.id) {
      case 'create-tank':
        return <CreateTankStep />;
      case 'capability':
        return <CapabilityStep />;
      case 'first-value':
        return <FirstValueStep />;
      case 'finish':
        return <FinishStep />;
      default: {
        // Exhaustiveness guard: if a new OnboardingStepId is added without a
        // matching case above, this assignment fails to type-check. The null
        // return keeps a safe runtime fallback.
        const _exhaustive: never = currentStepInfo.id;
        void _exhaustive;
        return null;
      }
    }
  };

  if (!ready) {
    return (
      <Container size="sm" py="xl" data-testid="welcome-wizard-loading">
        <Center mih="60vh">
          <Loader />
        </Center>
      </Container>
    );
  }

  return (
    <Container size="sm" py="xl" data-testid="welcome-wizard">
      <Stack gap="xl" align="stretch">
        <Group justify="space-between" align="center" wrap="nowrap">
          {/* Spacer keeps the wordmark centered while the skip sits top-right. */}
          <div style={{ flex: 1 }} />
          {/* The wizard is a standalone flow on the app's WHITE background, not
              the dark header, so it uses the dark-on-light brand token
              (teal[7] #116b66, 6.3:1 on white). --rdh-brand-accent — the
              header's token — is 1.22:1 here, i.e. an invisible wordmark. */}
          <Text
            ta="center"
            size="xl"
            fw={700}
            c="var(--rdh-brand-on-light)"
            style={{ letterSpacing: '0.5px' }}
          >
            Reef DataHub
          </Text>
          <div style={{ flex: 1, textAlign: 'right' }}>
            {canSkipSetup && (
              <Anchor
                component="button"
                type="button"
                c="dimmed"
                size="sm"
                onClick={() => void handleSkipSetup()}
                disabled={isSkipping}
                aria-busy={isSkipping}
                data-testid="wizard-skip-setup"
              >
                {isSkipping ? 'Skipping…' : 'Skip setup'}
              </Anchor>
            )}
          </div>
        </Group>

        <Stepper
          active={state.currentStep}
          size="sm"
          styles={{ steps: { flexWrap: 'wrap' } }}
        >
          {ONBOARDING_STEPS.map((step) => (
            <Stepper.Step key={step.id} label={step.label} />
          ))}
        </Stepper>

        <div data-testid="wizard-step-content">{renderStep()}</div>
      </Stack>
    </Container>
  );
}

/**
 * `/welcome` onboarding wizard. Wraps content in its own reducer-backed
 * provider (cloned from the EquipmentSetupWizard pattern).
 */
export function WelcomeWizard() {
  return (
    <OnboardingWizardProvider>
      <WelcomeWizardContent />
    </OnboardingWizardProvider>
  );
}
